import { FC, useContext, useRef } from "react";

import { datadogLogs } from "@datadog/browser-logs";
import { Box } from "@mui/material";
import { PayPalOneTimePaymentButton, VenmoOneTimePaymentButton } from "@paypal/react-paypal-js/sdk-v6";
import { FormType, PaymentMethod } from "@simplyk/common";

import { FrontendFormContext } from "@/common/contexts/FrontendFormContext";

interface PayPalDirectButtonProps {
  selectedPaymentMethod: PaymentMethod.PayPal | PaymentMethod.Venmo;
  createPaypalOrder: (paymentMethod: PaymentMethod.PayPal | PaymentMethod.Venmo) => Promise<string | null>;
  capturePaypal: (params: { orderId: string }) => Promise<void>;
}

export const PayPalDirectButton: FC<PayPalDirectButtonProps> = ({
  selectedPaymentMethod,
  createPaypalOrder,
  capturePaypal,
}) => {
  const { formType } = useContext(FrontendFormContext);
  const paypalButtonType = formType === FormType.DonationForm ? "donate" : "pay";
  const overlayRef = useRef<HTMLDivElement>(null);
  const loadingOverlayRef = useRef<HTMLDivElement>(null);
  const pendingOrderIdRef = useRef<string | null>(null);
  const isProcessingRef = useRef(false);

  const showLoading = () => {
    if (loadingOverlayRef.current) {
      loadingOverlayRef.current.style.display = "flex";
    }
  };

  const hideLoading = () => {
    if (loadingOverlayRef.current) {
      loadingOverlayRef.current.style.display = "none";
    }
  };

  const handleCreateOrder = async () => {
    const orderId = pendingOrderIdRef.current;
    if (!orderId) {
      return new Promise<{ orderId: string }>(() => {});
    }
    pendingOrderIdRef.current = null;
    isProcessingRef.current = false;
    return { orderId };
  };

  const handleApprove = async ({ orderId }: { orderId: string }) => {
    hideLoading();
    await capturePaypal({ orderId });
  };

  const handleError = (error: unknown) => {
    hideLoading();
    const message = String(error) || "PayPal SDK error";
    datadogLogs.logger.error("paypal.sdk.error", { message, paymentMethod: selectedPaymentMethod });
  };

  const handleOverlayClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isProcessingRef.current) {
      return;
    }
    isProcessingRef.current = true;
    const clientX = e.clientX;
    const clientY = e.clientY;
    let handedOff = false;

    try {
      showLoading();

      const orderId = await createPaypalOrder(selectedPaymentMethod);
      if (!orderId) {
        hideLoading();
        return;
      }

      pendingOrderIdRef.current = orderId;
      handedOff = true;

      hideLoading();

      if (overlayRef.current) {
        overlayRef.current.style.pointerEvents = "none";
        const syntheticClick = new MouseEvent("click", {
          bubbles: true,
          cancelable: true,
          clientX,
          clientY,
        });
        document.elementFromPoint(clientX, clientY)?.dispatchEvent(syntheticClick);

        showLoading();

        setTimeout(() => {
          if (overlayRef.current) {
            overlayRef.current.style.pointerEvents = "auto";
          }
          isProcessingRef.current = false;
        }, 500);
      }
    } finally {
      if (!handedOff) {
        isProcessingRef.current = false;
      }
    }
  };

  const sdkButton =
    selectedPaymentMethod === PaymentMethod.Venmo ? (
      <Box sx={{ "& venmo-button": { width: "100%" } }}>
        <VenmoOneTimePaymentButton
          presentationMode="auto"
          createOrder={handleCreateOrder}
          onApprove={handleApprove}
          onCancel={hideLoading}
          onError={handleError}
        />
      </Box>
    ) : (
      <Box sx={{ "& paypal-button": { width: "100%" } }}>
        <PayPalOneTimePaymentButton
          type={paypalButtonType}
          presentationMode="auto"
          commit
          createOrder={handleCreateOrder}
          onApprove={handleApprove}
          onCancel={hideLoading}
          onError={handleError}
        />
      </Box>
    );

  return (
    <Box sx={{ position: "relative", width: "100%", minHeight: 45 }}>
      {sdkButton}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <Box
        aria-hidden
        ref={overlayRef}
        component="div"
        sx={{ position: "absolute", inset: 0, cursor: "pointer", zIndex: 1 }}
        onClick={handleOverlayClick}
      />
      <Box
        ref={loadingOverlayRef}
        sx={{
          display: "none",
          position: "absolute",
          inset: 0,
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "rgba(255, 255, 255, 0.7)",
          zIndex: 2,
          borderRadius: "5px",
        }}
      >
        <Box
          component="span"
          sx={{
            width: 24,
            height: 24,
            border: "3px solid rgba(0, 0, 0, 0.1)",
            borderTopColor: "#333",
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
            "@keyframes spin": {
              to: { transform: "rotate(360deg)" },
            },
          }}
        />
      </Box>
    </Box>
  );
};
